<script setup lang="ts">
import { IconCircleCheck } from '@tabler/icons-vue'
import { computed } from 'vue'

import BaseIcon from '@tix/ds/components/atoms/BaseIcon.vue'

import RichCard, { type RichCardProps } from '@/components/RichCard.vue'

export type SelectableCardProps = Omit<RichCardProps, 'hoverable' | 'is'> & {
  /** Unique identifier for the card */
  id: string
  /** Whether this card is currently selected */
  isSelected?: boolean
  /** Whether this card is disabled */
  isDisabled?: boolean
  /** ARIA role for the button. Use `checkbox` for multiple selection, `radio` for single selection. */
  role?: 'button' | 'radio' | 'checkbox'
  /** Show checkmark badge when selected */
  togglable?: boolean
  // content props that the rich card component doesn't support natively
  /** Price text (supports HTML) */
  price?: string
  /** Dates text (supports HTML) */
  dates?: string
}

const props = withDefaults(defineProps<SelectableCardProps>(), {
  role: 'button',
  headingLevel: 'h3',
})

const emit = defineEmits<{
  click: [id: string]
}>()

const handleClick = () => emit('click', props.id)

const ariaChecked = computed(() => (props.role === 'radio' || props.role === 'checkbox' ? props.isSelected : undefined))

const ariaPressed = computed(() => (props.role === 'button' ? props.isSelected : undefined))
</script>

<template>
  <RichCard
    is="button"
    :aria-checked="ariaChecked"
    :aria-pressed="ariaPressed"
    :class="['selectable-card', props.class, { selected: isSelected, 'with-image': imageUrl }]"
    :disabled="isDisabled"
    :hoverable="true"
    :imageAlt="imageAlt"
    :imageUrl="imageUrl"
    :role="role"
    type="button"
    @click="handleClick"
  >
    <template v-if="togglable && isSelected" #overlay-slot>
      <div aria-hidden="true" class="checkmark">
        <BaseIcon :class="imageUrl ? 'check-lg' : 'check-sm'" :icon="IconCircleCheck" />
      </div>
    </template>

    <template #header-slot>
      <div class="header">
        <component :is="headingLevel" class="title">
          {{ title }}
        </component>
        <span v-if="price" class="price" v-html="price" />
      </div>
    </template>

    <template #description-slot>
      <div v-if="description || dates" class="description">
        <div v-if="dates" class="header" v-html="dates" />
        <div class="body" v-html="description" />
      </div>
    </template>
  </RichCard>
</template>

<style scoped>
.selectable-card {
  /* Local Themable Variables */
  --_color-selected: var(--action-card-border-active);
  --_background-selected: var(--action-card-bg-active);
  --_font-size-md: var(--selectable-card-font-size-md, var(--rich-card-font-size-md, var(--size-md)));
  --_font-size-sm: var(--selectable-card-font-size-sm, var(--rich-card-font-size-sm, var(--size-sm)));

  text-align: left;
  cursor: pointer;
  position: relative;

  &.selected,
  &.selected:hover {
    border-color: var(--_color-selected);
    outline: 1px solid var(--_color-selected);
    background: var(--_background-selected);

    &:focus-visible {
      outline: 2px solid var(--_color-selected);
      outline-offset: 2px;
      background: var(--_background-selected);
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  .check-lg {
    font-size: 16px; /* BaseIcon 1.5em → 24px */
  }

  .check-sm {
    font-size: 13.333px; /* BaseIcon 1.5em → 20px */
  }

  .checkmark {
    position: absolute;
    top: calc(var(--space-0-5) - 2px);
    right: calc(var(--space-0-5) - 2px);
    z-index: 10;
    color: var(--_color-selected);
    animation: circle-check-appear var(--transition-duration) ease-out;

    /* white background to fill the checkmark core */
    &::before {
      z-index: -1;
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: var(--action-card-bg);
      /* Clip the to only cover the icon circle */
      clip-path: circle(37% at 50% 50%);
    }

    :deep(svg) {
      display: block;
      filter: drop-shadow(0px 0.5px 0.5px rgba(0, 0, 0, 0.3));
    }
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-1);
    font-weight: var(--font-weight-semibold);
    font-size: var(--_font-size-md);

    @media (--mobile-experience) {
      gap: var(--space-0-25);
    }

    /* Slot content needs its own styles since RichCard's scoped styles don't apply */
    .title {
      margin: 0;
      font-family: var(--selectable-card-font-family, inherit);
      font-weight: inherit;
      font-size: inherit;
    }

    .price {
      color: var(--text-link-hover);
      flex-shrink: 1;
      min-width: 0;
      text-align: right;

      @media (--desktop-experience) {
        white-space: nowrap;
        flex-shrink: 0;
        width: fit-content;
      }
    }
  }

  .description {
    display: flex;
    flex-direction: column;
    gap: var(--space-0-25);

    font-size: var(--_font-size-sm);
    line-height: var(--line-height-body);
    color: var(--text-secondary);

    .header {
      font-weight: var(--font-weight-semibold);
    }
  }
}

@keyframes circle-check-appear {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
